import { Recipe } from "./types";

// Appetizer Recipes - Single source of truth for all appetizer descriptions
export const appetizerRecipes: Record<string, Recipe> = {
  // Soups
  "greek-lentil-soup": {
    id: "greek-lentil-soup",
    name: "Greek Lentil Soup",
    nameFr: "Soupe grecque aux lentilles",
    nameKey: "recipes.greekLentilSoup.name",
    description:
      "Hearty traditional Greek soup with tender lentils, vegetables, and Mediterranean herbs",
    descriptionFr:
      "Soupe grecque traditionnelle aux lentilles tendres, légumes et herbes méditerranéennes",
    descriptionKey: "recipes.greekLentilSoup.description",
    vegetarian: true,
  },

  "soup-of-the-day": {
    id: "soup-of-the-day",
    name: "Soup of the Day",
    nameFr: "Soupe du jour",
    nameKey: "recipes.soupOfTheDay.name",
    description:
      "Chef's freshly prepared daily soup made with seasonal ingredients",
    descriptionFr:
      "Soupe du jour fraîchement préparée par le chef avec des ingrédients de saison",
    descriptionKey: "recipes.soupOfTheDay.description",
  },

  // Salads
  "chef-salad": {
    id: "chef-salad",
    name: "Chef Salad",
    nameFr: "Salade du chef",
    nameKey: "recipes.chefSalad.name",
    description:
      "Fresh mixed greens with seasonal vegetables and chef's signature dressing",
    descriptionFr:
      "Verdures fraîches mélangées avec légumes de saison et vinaigrette signature du chef",
    descriptionKey: "recipes.chefSalad.description",
    vegetarian: true,
  },

  "caesar-salad": {
    id: "caesar-salad",
    name: "Caesar Salad",
    nameFr: "Salade césar",
    nameKey: "recipes.caesarSalad.name",
    description:
      "Crisp romaine lettuce tossed in creamy Caesar dressing, topped with garlic croutons and fresh parmesan",
    descriptionFr:
      "Laitue romaine croquante mélangée à une vinaigrette César crémeuse, garnie de croûtons à l'ail et parmesan frais",
    descriptionKey: "recipes.caesarSalad.description",
    vegetarian: true,
  },

  // Breads
  "garlic-bread": {
    id: "garlic-bread",
    name: "Garlic Bread",
    nameFr: "Pain à l'ail",
    nameKey: "recipes.garlicBread.name",
    description:
      "Freshly baked bread toasted to perfection with aromatic garlic butter",
    descriptionFr:
      "Pain fraîchement cuit, grillé à la perfection avec du beurre à l'ail aromatique",
    descriptionKey: "recipes.garlicBread.description",
    vegetarian: true,
  },

  "garlic-bread-mozzarella": {
    id: "garlic-bread-mozzarella",
    name: "Garlic Bread with Mozzarella",
    nameFr: "Pain à l'ail gratiné avec fromage mozzarella",
    nameKey: "recipes.garlicBreadMozzarella.name",
    description:
      "Toasted garlic bread topped with melted mozzarella cheese, baked golden brown",
    descriptionFr:
      "Pain à l'ail grillé garni de fromage mozzarella fondu, cuit au four jusqu'à doré",
    descriptionKey: "recipes.garlicBreadMozzarella.description",
    vegetarian: true,
  },

  "pita-bread": {
    id: "pita-bread",
    name: "Pita Bread",
    nameFr: "Pain pita",
    nameKey: "recipes.pitaBread.name",
    description: "Warm, fluffy pita bread",
    descriptionFr: "Pain pita chaud et moelleux",
    descriptionKey: "recipes.pitaBread.description",
    vegetarian: true,
  },

  // Greek Dips & Spreads
  tzatziki: {
    id: "tzatziki",
    name: "Tzatziki",
    nameFr: "Tzatziki",
    nameKey: "recipes.tzatziki.name",
    description:
      "Creamy Greek yogurt dip with fresh cucumbers, garlic, and herbs",
    descriptionFr:
      "Trempette crémeuse au yogourt grec avec concombres frais, ail et herbes. Servie avec pain pita chaud",
    descriptionKey: "recipes.tzatziki.description",
    vegetarian: true,
    glutenFree: true,
  },

  taramosalata: {
    id: "taramosalata",
    name: "Taramosalata",
    nameFr: "Taramosalata",
    nameKey: "recipes.taramosalata.name",
    description:
      "Smooth and delicate fish roe spread blended with potatoes, olive oil, and lemon",
    descriptionFr:
      "Tartinade délicate d'œufs de poisson mélangée avec pommes de terre, huile d'olive et citron. Servie avec pain pita chaud",
    descriptionKey: "recipes.taramosalata.description",
  },

  // Greek Appetizers
  "feta-olives": {
    id: "feta-olives",
    name: "Feta Cheese and Black Olives",
    nameFr: "Fromage feta et olives noires",
    nameKey: "recipes.fetaOlives.name",
    description:
      "Authentic Greek feta cheese paired with marinated Kalamata olives, drizzled with extra virgin olive oil",
    descriptionFr:
      "Fromage feta grec authentique accompagné d'olives Kalamata marinées, arrosé d'huile d'olive extra vierge",
    descriptionKey: "recipes.fetaOlives.description",
    vegetarian: true,
    glutenFree: true,
  },

  dolmades: {
    id: "dolmades",
    name: "Dolmades",
    nameFr: "Dolmades",
    nameKey: "recipes.dolmades.name",
    description:
      "Tender grape leaves stuffed with seasoned rice, fresh herbs, and Mediterranean spices",
    descriptionFr:
      "Feuilles de vigne tendres farcies de riz assaisonné, herbes fraîches et épices méditerranéennes",
    descriptionKey: "recipes.dolmades.description",
    vegetarian: true,
    glutenFree: true,
  },

  spanakopita: {
    id: "spanakopita",
    name: "Spanakopita",
    nameFr: "Spanakopita",
    nameKey: "recipes.spanakopita.name",
    description:
      "Flaky phyllo pastry filled with fresh spinach, feta cheese, onions, and aromatic herbs",
    descriptionFr:
      "Pâte phyllo feuilletée garnie d'épinards frais, fromage feta, oignons et herbes aromatiques",
    descriptionKey: "recipes.spanakopita.description",
    vegetarian: true,
  },

  "marinated-artichokes": {
    id: "marinated-artichokes",
    name: "Marinated Artichoke Hearts",
    nameFr: "Coeurs d'artichauts marinés",
    nameKey: "recipes.marinatedArtichokes.name",
    description:
      "Tender artichoke hearts marinated in olive oil, lemon, and Mediterranean herbs",
    descriptionFr:
      "Cœurs d'artichauts tendres marinés dans l'huile d'olive, citron et herbes méditerranéennes",
    descriptionKey: "recipes.marinatedArtichokes.description",
    vegetarian: true,
    glutenFree: true,
  },

  "marinated-hearts-of-palm": {
    id: "marinated-hearts-of-palm",
    name: "Marinated Hearts of Palm",
    nameFr: "Coeurs de palmier marinés",
    nameKey: "recipes.marinatedHeartsOfPalm.name",
    description:
      "Delicate hearts of palm marinated in a light citrus and herb dressing",
    descriptionFr:
      "Cœurs de palmier délicats marinés dans une vinaigrette légère aux agrumes et herbes",
    descriptionKey: "recipes.marinatedHeartsOfPalm.description",
    vegetarian: true,
    glutenFree: true,
  },

  // Baked/Gratinated Items
  "baked-escargots": {
    id: "baked-escargots",
    name: "Baked Escargots",
    nameFr: "Escargots gratinés",
    nameKey: "recipes.bakedEscargots.name",
    description:
      "French escargots baked in rich garlic-herb butter, served bubbling hot",
    descriptionFr:
      "Escargots français cuits au four dans un riche beurre à l'ail et aux herbes, servis bien chauds",
    descriptionKey: "recipes.bakedEscargots.description",
    glutenFree: true,
  },

  "baked-matane-shrimp": {
    id: "baked-matane-shrimp",
    name: "Baked Matane Shrimp",
    nameFr: "Crevettes de Matane gratinées",
    nameKey: "recipes.bakedMataneShrimp.name",
    description:
      "Sweet Matane shrimp baked with garlic butter, herbs, and a golden cheese topping",
    descriptionFr:
      "Crevettes de Matane sucrées cuites au four avec beurre à l'ail, herbes et gratinées au fromage doré",
    descriptionKey: "recipes.bakedMataneShrimp.description",
    glutenFree: true,
  },

  "baked-mushrooms": {
    id: "baked-mushrooms",
    name: "Baked Mushrooms",
    nameFr: "Champignons gratinés",
    nameKey: "recipes.bakedMushrooms.name",
    description:
      "Fresh mushrooms baked with garlic, herbs, and melted cheese until golden",
    descriptionFr:
      "Champignons frais cuits au four avec ail, herbes et fromage fondu jusqu'à doré",
    descriptionKey: "recipes.bakedMushrooms.description",
    vegetarian: true,
    glutenFree: true,
  },

  // Fried & Grilled Items
  "chicken-wings": {
    id: "chicken-wings",
    name: "Chicken Wings",
    nameFr: "Ailes de poulet",
    nameKey: "recipes.chickenWings.name",
    description:
      "Crispy chicken wings available in your choice of spicy or mild sauce",
    descriptionFr:
      "Ailes de poulet croustillantes disponibles avec sauce piquante ou douce au choix",
    descriptionKey: "recipes.chickenWings.description",
    glutenFree: true,
  },

  "butterfly-shrimp-4": {
    id: "butterfly-shrimp-4",
    name: "Butterfly Shrimp (4)",
    nameFr: "Crevettes papillon (4)",
    nameKey: "recipes.butterflyShrimp4.name",
    description:
      "Four succulent shrimp butterflied, breaded, and fried to crispy golden perfection",
    descriptionFr:
      "Quatre crevettes succulentes panées et frites jusqu'à une perfection croustillante et dorée",
    descriptionKey: "recipes.butterflyShrimp4.description",
  },

  "scallops-appetizer": {
    id: "scallops",
    name: "Coquille Saint-Jacques",
    nameFr: "Coquille Saint-Jacques",
    nameKey: "recipes.scallops.name",
    description:
      "Mixed seafood with scallops, seared or baked to tender perfection",
    descriptionFr:
      "Fruits de mer variés avec pétoncles, poêlés ou cuits au four à la perfection tendre",
    descriptionKey: "recipes.scallops.description",
    glutenFree: true,
  },

  "spicy-sausages-appetizer": {
    id: "spicy-sausages",
    name: "Spicy Sausages",
    nameFr: "Saucisses piquantes",
    nameKey: "recipes.spicySausages.name",
    description:
      "Grilled spicy sausages seasoned with Mediterranean spices and herbs",
    descriptionFr:
      "Saucisses piquantes grillées assaisonnées d'épices et herbes méditerranéennes",
    descriptionKey: "recipes.spicySausages.description",
    spicy: true,
    glutenFree: true,
  },

  "fried-calamari-1": {
    id: "fried-calamari-1",
    name: "Fried Calamari (for 1)",
    nameFr: "Calmars frits (pour 1)",
    nameKey: "recipes.friedCalamari1.name",
    description:
      "Tender calamari rings lightly breaded and fried until crispy. Served with zesty cocktail sauce",
    descriptionFr:
      "Anneaux de calmars tendres légèrement panés et frits jusqu'à croustillants. Servis avec sauce cocktail relevée",
    descriptionKey: "recipes.friedCalamari1.description",
  },

  "fried-calamari-2": {
    id: "fried-calamari-2",
    name: "Fried Calamari (for 2)",
    nameFr: "Calmars frits (pour 2)",
    nameKey: "recipes.friedCalamari2.name",
    description:
      "Generous portion of tender calamari rings lightly breaded and fried until crispy. Served with zesty cocktail sauce",
    descriptionFr:
      "Portion généreuse d'anneaux de calmars tendres légèrement panés et frits jusqu'à croustillants. Servis avec sauce cocktail relevée",
    descriptionKey: "recipes.friedCalamari2.description",
  },

  // Platters
  "pikilia-1": {
    id: "pikilia-1",
    name: "Pikilia (for 1)",
    nameFr: "Pikilia (pour 1)",
    nameKey: "recipes.pikilia1.name",
    description:
      "Traditional Greek appetizer platter featuring tzatziki, taramosalata, spanakopita, dolmades, feta cheese, Kalamata olives, marinated hearts of palm and artichokes, fresh tomatoes, and cucumbers",
    descriptionFr:
      "Plateau d'entrées grecques traditionnel avec tzatziki, taramosalata, spanakopita, dolmades, fromage feta, olives Kalamata, cœurs de palmiers et d'artichauts marinés, tomates et concombres frais",
    descriptionKey: "recipes.pikilia1.description",
    vegetarian: true,
  },

  "pikilia-2": {
    id: "pikilia-2",
    name: "Pikilia (for 2)",
    nameFr: "Pikilia (pour 2)",
    nameKey: "recipes.pikilia2.name",
    description:
      "Generous Greek appetizer platter for sharing featuring tzatziki, taramosalata, spanakopita, dolmades, feta cheese, Kalamata olives, marinated hearts of palm and artichokes, fresh tomatoes, and cucumbers",
    descriptionFr:
      "Généreux plateau d'entrées grecques à partager avec tzatziki, taramosalata, spanakopita, dolmades, fromage feta, olives Kalamata, cœurs de palmiers et d'artichauts marinés, tomates et concombres frais",
    descriptionKey: "recipes.pikilia2.description",
    vegetarian: true,
  },
};
